import { Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PodPhase, SitePod } from '@/types';
import { formatAge } from './shared';

function PodPhaseBadge({ phase }: { phase: PodPhase }) {
  const colors: Record<PodPhase, string> = {
    Running: 'bg-status-healthy/20 text-status-healthy',
    Pending: 'bg-status-warning/20 text-status-warning',
    Failed: 'bg-status-error/20 text-status-error',
    Succeeded: 'bg-silver-muted/20 text-silver-muted',
    Unknown: 'bg-silver-muted/20 text-silver-muted',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[phase])}>
      {phase}
    </span>
  );
}

export function PodTable({ pods, namespace, nodeName }: { pods: SitePod[]; namespace?: string; nodeName?: string }) {
  const filtered = pods.filter((p) => {
    if (namespace && p.namespace !== namespace) return false;
    if (nodeName && p.nodeName !== nodeName) return false;
    return true;
  });

  if (filtered.length === 0) {
    return (
      <div className="text-center py-12 space-y-3">
        <Activity size={40} className="mx-auto text-silver-muted/30" />
        <p className="text-silver-muted">No pods found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-elevated text-left">
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Name</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Namespace</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Status</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Ready</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Node</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Restarts</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Age</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((pod) => (
            <tr
              key={`${pod.namespace}/${pod.name}`}
              className="border-b border-surface-elevated last:border-b-0 hover:bg-surface-elevated/30 transition-colors"
            >
              <td className="px-4 py-3">
                <div>
                  <span className="text-silver-bright font-mono text-xs">{pod.name}</span>
                  {pod.image && (
                    <p className="text-[10px] text-silver-muted font-mono mt-0.5 truncate max-w-xs">
                      {pod.image}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-xs text-silver-muted font-mono">{pod.namespace}</span>
              </td>
              <td className="px-4 py-3">
                <PodPhaseBadge phase={pod.phase} />
              </td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'w-2 h-2 rounded-full inline-block',
                    pod.ready ? 'bg-status-healthy' : 'bg-silver-muted/30',
                  )}
                />
              </td>
              <td className="px-4 py-3 text-xs text-silver-muted font-mono">
                {pod.nodeName || '--'}
              </td>
              <td className="px-4 py-3 text-xs text-silver-muted">
                {pod.restarts}
              </td>
              <td className="px-4 py-3 text-xs text-silver-muted">
                {formatAge(pod.startedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
